import { useState } from 'react'
import { useGameStore } from '@/store/gameState'
import { ECHO_LIST, type Echo } from '@/data/echoes'
import { RARITY_COLORS } from '@/data/constants'
import styles from './CodexView.module.css'

export function CodexView() {
  const discovered = useGameStore((s) => s.discovered)
  const glimmering = useGameStore((s) => s.glimmering)
  const sightings = useGameStore((s) => s.sightings)

  const [selectedEcho, setSelectedEcho] = useState<{ echo: Echo; index: number } | null>(null)

  const totalEchoes = ECHO_LIST.length
  const discoveredCount = discovered.length

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.title}>Echo Codex</h2>
        <span className={styles.count}>
          {discoveredCount}/{totalEchoes}
        </span>
      </header>

      <div className={`${styles.grid} stagger`}>
        {ECHO_LIST.map((echo, index) => {
          const isDiscovered = discovered.includes(echo.id)
          const isGlimmering = glimmering.includes(echo.id)
          const sightingCount = sightings[echo.id] ?? 0
          const echoNumber = String(index + 1).padStart(3, '0')

          return (
            <button
              key={echo.id}
              className={`${styles.echoCard} ${isDiscovered ? styles.discovered : styles.undiscovered}`}
              onClick={() => isDiscovered && setSelectedEcho({ echo, index })}
              disabled={!isDiscovered}
              style={{
                '--rarity-color': RARITY_COLORS[echo.rarity],
              } as React.CSSProperties}
            >
              <div className={styles.echoImageWrapper}>
                {isDiscovered ? (
                  <img
                    src={echo.image}
                    alt={echo.name}
                    className={`${styles.echoImage} ${isGlimmering ? styles.glimmering : ''}`}
                  />
                ) : (
                  <div className={styles.silhouette}>
                    <span className={styles.questionMark}>?</span>
                  </div>
                )}
                {isGlimmering && (
                  <div className={styles.glimmerBadge}>✨</div>
                )}
              </div>
              <div className={styles.echoInfo}>
                <span className={styles.echoNumber}>{echoNumber}</span>
                <span className={styles.echoName}>
                  {isDiscovered ? echo.name : '???'}
                </span>
                {isDiscovered && (
                  <span className={styles.sightings}>
                    ×{sightingCount}
                  </span>
                )}
              </div>
              <div
                className={styles.rarityBar}
                style={{ background: RARITY_COLORS[echo.rarity] }}
              />
            </button>
          )
        })}
      </div>

      {/* Echo Detail Modal */}
      {selectedEcho && (
        <EchoDetailModal
          echo={selectedEcho.echo}
          echoNumber={String(selectedEcho.index + 1).padStart(3, '0')}
          sightings={sightings[selectedEcho.echo.id] ?? 0}
          isGlimmering={glimmering.includes(selectedEcho.echo.id)}
          onClose={() => setSelectedEcho(null)}
        />
      )}
    </div>
  )
}

interface EchoDetailModalProps {
  echo: Echo
  echoNumber: string
  sightings: number
  isGlimmering: boolean
  onClose: () => void
}

function EchoDetailModal({ echo, echoNumber, sightings, isGlimmering, onClose }: EchoDetailModalProps) {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>×</button>

        <div className={styles.modalImage}>
          <img
            src={echo.image}
            alt={echo.name}
            className={isGlimmering ? styles.glimmering : ''}
          />
          {isGlimmering && (
            <div className={styles.glimmerOverlay} />
          )}
        </div>

        <span className={styles.modalNumber}>{echoNumber}</span>
        <h3 className={styles.modalName}>{echo.name}</h3>

        <span
          className={styles.modalRarity}
          style={{ color: RARITY_COLORS[echo.rarity] }}
        >
          {echo.rarity.toUpperCase()}
        </span>

        <p className={styles.modalConcept}>{echo.concept}</p>

        <div className={styles.modalMeta}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Sightings</span>
            <span className={styles.metaValue}>{sightings}</span>
          </div>
          {isGlimmering && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Variant</span>
              <span className={styles.metaValue}>✨ Glimmering</span>
            </div>
          )}
        </div>

        {/* Lore progress */}
        <div className={styles.loreProgress}>
          <span className={styles.loreTitle}>Lore Progress</span>
          <div className={styles.loreMilestones}>
            {[1, 3, 7, 15, 30].map((milestone) => (
              <div
                key={milestone}
                className={`${styles.loreMilestone} ${sightings >= milestone ? styles.loreUnlocked : ''}`}
              >
                {milestone}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
